/**
 * Action rapide délibération : voir + modifier les notes d'un étudiant + relancer
 * le calcul de sa moyenne en quelques secondes (sans recalculer tout le niveau).
 *
 * Pour chaque matière de chaque UE on peut éditer D1, D2, SO, SR.
 * À la sauvegarde, on ne ré-envoie QUE les notes effectivement modifiées,
 * puis on déclenche le recalcul de l'étudiant et on rafraîchit les données.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Loader, Save, Calculator, AlertTriangle, CheckCircle2,
  TrendingUp, ChevronRight,
} from 'lucide-react';
import { useGetDonneesReleveQuery } from '../../services/relevesApi';
import { useSaisieIndividuelleMutation as useSaisieDevoirMutation } from '../../services/notesDevoirsApi';
import { useSaisieIndividuelleMutation as useSaisieExamenMutation } from '../../services/notesExamensApi';
import { useCalculerToutEtudiantMutation } from '../../services/resultatsApi';

interface Props {
  open: boolean;
  etudiantId: number;
  matricule: string;
  nom: string;
  semestre: 1 | 2;
  anneeAcademique: string;
  moyenneActuelle: number | null;
  onClose: () => void;
  onApplied?: () => void;
}

type Champ = 'd1' | 'd2' | 'so' | 'sr';

interface EditValue {
  // Une entrée par matière_id+champ : la valeur saisie
  value: string;
  // True si le champ est marqué absent
  absent: boolean;
  // Valeur initiale pour ne ré-envoyer que les modifications réelles
  initialValue: string;
  initialAbsent: boolean;
}

function fmtNote(n: number | null | undefined): string {
  if (n === null || n === undefined) return '';
  return String(n);
}

function parseNote(v: string): number | null {
  const t = (v || '').trim().replace(',', '.');
  if (!t) return null;
  const n = parseFloat(t);
  if (isNaN(n) || n < 0 || n > 20) return null;
  return n;
}

const SEMS_STR: Record<1 | 2, 'S1' | 'S2'> = { 1: 'S1', 2: 'S2' };

const ActionRapideDeliberationModal: React.FC<Props> = ({
  open, etudiantId, matricule, nom, semestre, anneeAcademique, moyenneActuelle,
  onClose, onApplied,
}) => {
  const { data, isFetching, refetch } = useGetDonneesReleveQuery(
    { matricule, semestre, annee_academique: anneeAcademique },
    { skip: !open || !matricule },
  );

  const [saisieDevoir] = useSaisieDevoirMutation();
  const [saisieExamen] = useSaisieExamenMutation();
  const [calculerEtudiant] = useCalculerToutEtudiantMutation();

  // État local des éditions, clef = `${matiere_id}-${champ}`
  const [edits, setEdits] = useState<Record<string, EditValue>>({});
  const [saving, setSaving] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [moyenneAvant, setMoyenneAvant] = useState<number | null>(null);
  // Ref pour réinitialiser quand l'étudiant change
  const initialMatricule = useRef<string>('');

  useEffect(() => {
    if (!open) return;
    // Reset state à l'ouverture / changement d'étudiant
    if (initialMatricule.current !== matricule) {
      initialMatricule.current = matricule;
      setEdits({});
      setError(null); setSuccess(null);
      setMoyenneAvant(moyenneActuelle);
    }
  }, [open, matricule, moyenneActuelle]);

  // Pré-remplir les edits depuis les données chargées
  useEffect(() => {
    if (!data) return;
    const m: Record<string, EditValue> = {};
    data.ues.forEach((ue) => {
      ue.matieres.forEach((mat) => {
        m[`${mat.matiere_id}-d1`] = {
          value: fmtNote(mat.note_d1), absent: mat.absent_d1,
          initialValue: fmtNote(mat.note_d1), initialAbsent: mat.absent_d1,
        };
        m[`${mat.matiere_id}-d2`] = {
          value: fmtNote(mat.note_d2), absent: mat.absent_d2,
          initialValue: fmtNote(mat.note_d2), initialAbsent: mat.absent_d2,
        };
        // La note d'examen affichée est soit SO soit SR (selon est_rattrapage)
        // On garde le slot SO et SR séparés pour permettre l'édition des deux.
        const noteSO = mat.est_rattrapage ? null : mat.note_examen;
        const noteSR = mat.est_rattrapage ? mat.note_examen : null;
        m[`${mat.matiere_id}-so`] = {
          value: fmtNote(noteSO), absent: !mat.est_rattrapage && mat.absent_examen,
          initialValue: fmtNote(noteSO), initialAbsent: !mat.est_rattrapage && mat.absent_examen,
        };
        m[`${mat.matiere_id}-sr`] = {
          value: fmtNote(noteSR), absent: mat.est_rattrapage && mat.absent_examen,
          initialValue: fmtNote(noteSR), initialAbsent: mat.est_rattrapage && mat.absent_examen,
        };
      });
    });
    setEdits(m);
  }, [data]);

  const setCell = (matiereId: number, champ: Champ, patch: Partial<EditValue>) => {
    const key = `${matiereId}-${champ}`;
    setEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
    setSuccess(null);
  };

  // Liste des modifications réelles à envoyer
  const modifications = useMemo(() => {
    const out: Array<{
      matiereId: number; champ: Champ; value: number | null; absent: boolean;
    }> = [];
    Object.entries(edits).forEach(([key, v]) => {
      if (v.value === v.initialValue && v.absent === v.initialAbsent) return;
      const [mid, champ] = key.split('-') as [string, Champ];
      const parsed = v.absent ? null : parseNote(v.value);
      out.push({
        matiereId: Number(mid), champ,
        value: parsed,
        absent: v.absent,
      });
    });
    return out;
  }, [edits]);

  const handleSauvegarder = async (recalc: boolean) => {
    if (!data) return;
    setError(null); setSuccess(null);
    setSaving(true);
    try {
      const semStr = SEMS_STR[semestre];
      // Devoirs : on regroupe les modifications de D1/D2 par matière car le
      // backend attend les deux champs ensemble (saisie individuelle).
      const groupesDevoirs = new Map<number, { d1?: any; d2?: any }>();
      const examens: Array<{ matiereId: number; champ: 'so' | 'sr'; value: number | null; absent: boolean }> = [];

      for (const mod of modifications) {
        if (mod.champ === 'd1' || mod.champ === 'd2') {
          const g = groupesDevoirs.get(mod.matiereId) || {};
          g[mod.champ] = { value: mod.value, absent: mod.absent };
          groupesDevoirs.set(mod.matiereId, g);
        } else {
          examens.push({ matiereId: mod.matiereId, champ: mod.champ, value: mod.value, absent: mod.absent });
        }
      }

      // Sauvegarde devoirs : reprend les champs non-modifiés de la matière pour
      // ne pas écraser l'autre composant (D1/D2).
      // Array.from car target=ES5 ne supporte pas l'itération de Map.
      for (const [matiereId, patch] of Array.from(groupesDevoirs.entries())) {
        const d1Cur = edits[`${matiereId}-d1`];
        const d2Cur = edits[`${matiereId}-d2`];
        const d1 = patch.d1 || { value: d1Cur.absent ? null : parseNote(d1Cur.value), absent: d1Cur.absent };
        const d2 = patch.d2 || { value: d2Cur.absent ? null : parseNote(d2Cur.value), absent: d2Cur.absent };
        await saisieDevoir({
          etudiant_id: etudiantId,
          matiere_id: matiereId,
          annee_academique: anneeAcademique,
          semestre: semStr,
          note1: d1.value, absent1: d1.absent,
          note2: d2.value, absent2: d2.absent,
        }).unwrap();
      }

      // Sauvegarde examens (SO ou SR sont des enregistrements séparés)
      for (const ex of examens) {
        await saisieExamen({
          etudiant_id: etudiantId,
          matiere_id: ex.matiereId,
          annee_academique: anneeAcademique,
          semestre: semStr,
          note_examen: ex.value,
          absent: ex.absent,
          est_rattrapage: ex.champ === 'sr',
        }).unwrap();
      }

      // Recalcul :
      // - Si des modifs ont été sauvegardées, saisieDevoir/saisieExamen ont DÉJÀ
      //   déclenché recalculer_pour_matiere côté backend (ECUE + UE + Semestre
      //   pour la/les matières touchées). Appeler calculer_tout_etudiant ici
      //   ferait tourner toutes les matières du niveau → timeout gunicorn.
      // - Si aucune modif : l'utilisateur veut forcer un recalc complet, on l'appelle.
      if (recalc && modifications.length === 0) {
        setCalcLoading(true);
        await calculerEtudiant({ etudiant_id: etudiantId }).unwrap();
        setCalcLoading(false);
      }

      // Recharger les données
      await refetch();
      setSuccess(
        modifications.length > 0
          ? `${modifications.length} modification(s) enregistrée(s) — moyenne mise à jour automatiquement.`
          : 'Recalcul effectué.',
      );
      if (onApplied) onApplied();
    } catch (e: any) {
      setError(e?.data?.detail || e?.data?.error || e?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
      setCalcLoading(false);
    }
  };

  if (!open) return null;

  const nouvelleMoyenne = data?.resultat_semestre?.moyenne_semestre ?? null;
  const delta = (nouvelleMoyenne !== null && moyenneAvant !== null)
    ? nouvelleMoyenne - moyenneAvant : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calculator size={15} className="text-blue-600 dark:text-blue-400" />
              Délibération rapide — <span className="font-mono">{matricule}</span> · {nom}
            </h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {data?.etudiant.classe} · S{semestre} · {anneeAcademique}
              {data?.etudiant.option && ` · ${data.etudiant.option}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <KpiMoyenne label="Moy. avant" value={moyenneAvant} />
            <ChevronRight size={14} className="text-gray-400" />
            <KpiMoyenne label="Moy. actuelle" value={nouvelleMoyenne}
              delta={delta} />
            <button onClick={onClose}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="px-5 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300 flex items-center gap-2">
            <AlertTriangle size={13} /> {error}
          </div>
        )}
        {success && (
          <div className="px-5 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-300 flex items-center gap-2">
            <CheckCircle2 size={13} /> {success}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isFetching && !data && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader size={28} className="animate-spin text-blue-500 mb-2" />
              <p className="text-sm text-gray-500">Chargement…</p>
            </div>
          )}
          {data && (
            <div className="p-4 space-y-3">
              {data.ues.map((ue) => (
                <div key={ue.ue_id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs font-bold text-gray-700 dark:text-gray-200">
                      <span className="text-gray-500 font-mono mr-2">{ue.code_ue}</span>
                      {ue.nom_ue}
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className={`px-2 py-0.5 rounded font-semibold ${
                        ue.validee
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : ue.eliminatoire
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      }`}>
                        {ue.validee ? 'Validée' : ue.eliminatoire ? 'Éliminatoire' : 'Non validée'}
                      </span>
                      <span className="text-gray-500">
                        Moy {ue.moyenne_ue?.toFixed(2) ?? '—'}
                        {` · ${ue.credits_obtenus}/${ue.credits_total} crédits`}
                      </span>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/30 text-[10px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Matière</th>
                        <th className="px-2 py-1.5 text-center w-16 bg-blue-50 dark:bg-blue-900/20">D1</th>
                        <th className="px-2 py-1.5 text-center w-16 bg-blue-50 dark:bg-blue-900/20">D2</th>
                        <th className="px-2 py-1.5 text-center w-16">Moy.D</th>
                        <th className="px-2 py-1.5 text-center w-16 bg-green-50 dark:bg-green-900/20">SO</th>
                        <th className="px-2 py-1.5 text-center w-16 bg-orange-50 dark:bg-orange-900/20">SR</th>
                        <th className="px-2 py-1.5 text-center w-16">Moy.ECUE</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {ue.matieres.map((mat) => {
                        const moy = mat.moyenne_ecue;
                        return (
                          <tr key={mat.matiere_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                            <td className="px-2 py-1 text-left">
                              <div className="text-gray-900 dark:text-white font-medium truncate max-w-[260px]">
                                {mat.nom_matiere}
                              </div>
                              <div className="text-[10px] text-gray-500 font-mono">
                                {mat.code_matiere} · {mat.credits} cr.
                              </div>
                            </td>
                            <CellEditable matiereId={mat.matiere_id} champ="d1"
                              edits={edits} setCell={setCell} />
                            <CellEditable matiereId={mat.matiere_id} champ="d2"
                              edits={edits} setCell={setCell} />
                            <td className="px-2 py-1 text-center tabular-nums font-semibold">
                              {mat.moy_devoirs !== null
                                ? <span className={mat.moy_devoirs >= 10 ? 'text-green-600' : 'text-red-600'}>
                                    {mat.moy_devoirs.toFixed(2)}
                                  </span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <CellEditable matiereId={mat.matiere_id} champ="so"
                              edits={edits} setCell={setCell} />
                            <CellEditable matiereId={mat.matiere_id} champ="sr"
                              edits={edits} setCell={setCell} />
                            <td className="px-2 py-1 text-center tabular-nums font-bold">
                              {moy !== null
                                ? <span className={moy >= 10 ? 'text-green-600' : 'text-red-600'}>
                                    {moy.toFixed(2)}
                                  </span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <TrendingUp size={12} />
            {modifications.length === 0
              ? 'Aucune modification en attente'
              : `${modifications.length} modification(s) en attente`}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              Fermer
            </button>
            <button onClick={() => handleSauvegarder(false)}
              disabled={modifications.length === 0 || saving}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
              {saving && !calcLoading && <Loader size={12} className="animate-spin" />}
              <Save size={12} /> Enregistrer
            </button>
            <button onClick={() => handleSauvegarder(true)}
              disabled={saving}
              className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
              {(saving || calcLoading) && <Loader size={12} className="animate-spin" />}
              <Calculator size={12} />
              {modifications.length > 0 ? 'Enregistrer + recalculer' : 'Recalculer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function CellEditable({ matiereId, champ, edits, setCell }: {
  matiereId: number;
  champ: Champ;
  edits: Record<string, EditValue>;
  setCell: (matiereId: number, champ: Champ, patch: Partial<EditValue>) => void;
}) {
  const key = `${matiereId}-${champ}`;
  const v = edits[key];
  if (!v) return <td className="px-2 py-1 text-center text-gray-300">—</td>;
  const modifie = v.value !== v.initialValue || v.absent !== v.initialAbsent;
  const parsed = v.absent ? 0 : parseNote(v.value);
  const invalid = !v.absent && v.value !== '' && parsed === null;
  const champColor =
    champ === 'd1' || champ === 'd2' ? 'bg-blue-50/30 dark:bg-blue-900/10' :
    champ === 'so' ? 'bg-green-50/30 dark:bg-green-900/10' :
    'bg-orange-50/30 dark:bg-orange-900/10';
  return (
    <td className={`px-1 py-1 text-center ${champColor}`}>
      <div className="flex items-center gap-0.5 justify-center">
        <input type="text" value={v.absent ? 'Abs' : v.value}
          disabled={v.absent}
          onChange={(e) => setCell(matiereId, champ, { value: e.target.value })}
          className={`w-12 px-1 py-0.5 text-xs text-center tabular-nums border rounded ${
            invalid
              ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-700'
              : modifie
              ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 font-bold'
              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
          } text-gray-900 dark:text-gray-100`} />
        <label className="text-[8px] text-gray-400 cursor-pointer" title="Marquer absent">
          <input type="checkbox" checked={v.absent}
            onChange={(e) => setCell(matiereId, champ, { absent: e.target.checked, value: e.target.checked ? '' : v.initialValue })}
            className="scale-75 align-middle" />
        </label>
      </div>
    </td>
  );
}

function KpiMoyenne({ label, value, delta }: {
  label: string; value: number | null; delta?: number | null;
}) {
  const couleur = value === null ? 'text-gray-400'
    : value >= 10 ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className={`text-base font-bold tabular-nums ${couleur}`}>
        {value !== null ? value.toFixed(2) : '—'}
      </div>
      {delta !== null && delta !== undefined && Math.abs(delta) >= 0.01 && (
        <div className={`text-[9px] font-bold ${delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {delta > 0 ? '+' : ''}{delta.toFixed(2)}
        </div>
      )}
    </div>
  );
}

export default ActionRapideDeliberationModal;
